// Thin, safe wrapper around the Triple Whale pixel (installed in public/index.html).
// The snippet defines the TriplePixel queue stub synchronously, but retry anyway
// in case an event is sent before it has been evaluated (cap ~10s).
const send = (args, onSent) => {
    let attempts = 0;

    const fire = () => {
        if (window.TriplePixel) {
            window.TriplePixel(...args);
            if (onSent) onSent();
            return;
        }

        if (attempts++ < 25) {
            setTimeout(fire, 400);
        }
    };

    fire();
};

export const tripleWhale = (...args) => send(args);

// Custom events take the form TriplePixel('custom', '<name>', { ...props }). The
// name must match a Custom Event defined in the Triple Whale app, and that event
// has to be marked a conversion event there before it is attributed.
export const tripleWhaleCustom = (name, props) => send(['custom', name, props]);

// AddToCart is a standard event, and its item shape is NOT the { i, q, v } used by
// lineItems on the checkout/purchase events — it takes { item, q, v }.
//
// We sell one membership with no variants, so the variant id mirrors the product
// id: `v` is documented as required, and repeating the id keeps the catalog from
// filling up with empty variants.
export const tripleWhaleAddToCart = (item, quantity = 1) => (
    tripleWhale('AddToCart', { item, q: quantity, v: item })
);

// lineItems for the funnel and purchase events: product id, quantity, variant id.
// All three are required, and the variant id mirrors the product id for the same
// reason it does on AddToCart. product_id here matches the product_id on the
// server-side order line item, which is what lets Triple Whale join the two.
export const tripleWhaleLineItems = (productId, quantity = 1) => (
    productId ? [{ i: productId, q: quantity, v: productId }] : undefined
);

// There is no callable "view product" event. Triple Whale derives
// productView (ViewContent) in Sonar from a pageLoad that carries product
// context, so viewing a product in an SPA *is* a pageLoad with the product
// attached — see "Managing Context in SPAs", Option 1, in TW's pixel guide.
//
// Option 1 (context passed with the event) rather than Option 2 (assigning to
// window.TriplePixelData.product) so there is no global left behind to clear.
export const tripleWhaleProductView = ({ id, name, price }) => send(['pageLoad', {
    // variant mirrors the product id, as it does everywhere else here
    product: { id, name, variant: id, ...(price === undefined ? {} : { price }) }
}]);

// The keys the base snippet puts on window.TriplePixelData. Anything else on that
// object is per-page context attached for a single pageLoad.
const BASE_DATA_KEYS = ['TripleName', 'ver', 'plat', 'isHeadless'];

// Triple Whale asks that TriplePixelData be reset after each pageLoad so stale
// page context isn't attached to the next virtual pageview. Only the page-level
// keys are cleared — dropping the base config would break the pixel entirely.
export const tripleWhalePageLoad = () => {
    send(['pageLoad'], () => {
        const data = window.TriplePixelData;

        if (!data) {
            return;
        }

        Object.keys(data).forEach(key => {
            if (!BASE_DATA_KEYS.includes(key)) {
                delete data[key];
            }
        });
    });
};
